import { useEffect, useMemo, useState } from "react";
import type { CellContext, ColumnDef } from "@tanstack/react-table";
import {
  Button as GetiButton,
  DataTable,
  Dialog as GetiDialog,
  DialogContent as GetiDialogContent,
  DialogDescription as GetiDialogDescription,
  DialogHeader as GetiDialogHeader,
  DialogTitle as GetiDialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@geti/identidade-visual-react";
import { Textarea } from "../../../components/ui/textarea";
import { MdCreate, MdMoreHoriz, MdOutlinePushPin } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { PATH } from "../../../PATH";
import { isLoggedIn } from "../../../Auth/auth";
import { fetchFavoritePosts } from "../../../Services/Slices/favoritePostsSlice";
import { patchFavoritePost } from "../../../Services/Slices/patchFavoritePost";
import { patchPostDescription } from "../../../Services/Slices/updatePostDescriptionSlice";
import { updatePostPosition } from "../../../Services/Slices/updatePostPosition";
import { toast } from "sonner";
import styles from "./HomeFavoriteDataTable.module.css";

type FavoriteRow = {
  id: number;
  url: string;
  description: string;
};

interface HomeFavoriteDataTableProps {
  title?: string;
}

const PAGE_SIZE = 8;

export function HomeFavoriteDataTable({ title }: HomeFavoriteDataTableProps) {
  const dispatch = useDispatch<any>();
  const favoritePosts = useSelector((state: any) => state.favoritePostsSlice.data);
  const loading = useSelector((state: any) => state.updatePostPosition.loading);
  const error = useSelector((state: any) => state.updatePostPosition.error);

  const [posts, setPosts] = useState<FavoriteRow[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [caracterLimit, setCaracterLimit] = useState(false);
  const [message, setMessage] = useState("");
  const [description, setDescription] = useState("");
  const [fileName, setFileName] = useState("");
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [hoveredItemIndex, setHoveredItemIndex] = useState<number | null>(null);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    dispatch(fetchFavoritePosts());
  }, [dispatch]);

  useEffect(() => {
    if (favoritePosts?.favorite_posts) {
      setPosts(favoritePosts.favorite_posts);
    }
  }, [favoritePosts]);

  const cleanFile = (file: string) => file.split("file_key=")[1];

  const formatFileName = (value: string) => {
    return (
      value
        .split("---file=")[1]
        ?.replace("_", " ")
        .replace(".html", "") || ""
    );
  };

  const redirectToHTMLViewer = (fileKey: string) => {
    const url = `${PATH.baseApi}/render-html/?file_key=${cleanFile(fileKey)}`;
    window.open(url, "_blank");
  };

  const handleFavoriteButton = async (fileKey: string) => {
    const success = await dispatch(patchFavoritePost(cleanFile(fileKey)));
    dispatch(fetchFavoritePosts());
    if (success) {
      toast.success("Ato desafixado");
    } else {
      toast.error("Erro ao desafixar ato");
    }
  };

  const handleEditButton = (fileKey: string, currentDescription: string) => {
    setFileName(cleanFile(fileKey));
    setOpenDialog(true);
    setDescription(currentDescription);
    setMessage("");
  };

  const handleSubmitDescription = async () => {
    if (description.length > 0 && description.length <= 200) {
      setCaracterLimit(false);
      setOpenDialog(false);
      const success = await dispatch(patchPostDescription(fileName, description));
      dispatch(fetchFavoritePosts());
      if (success) {
        toast.success("Descrição atualizada");
      } else {
        toast.error("Erro ao atualizar descrição");
      }
      return;
    }

    if (description.length > 200) {
      setCaracterLimit(true);
      setMessage("Descrição maior que 200 caracteres");
      return;
    }

    setCaracterLimit(true);
    setMessage("Adicione uma descrição");
  };

  const handleDragStart = (index: number) => {
    if (!isLoggedIn()) return;
    setDraggedItemIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    if (!isLoggedIn()) return;
    event.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === index) return;

    const updatedPosts = [...posts];
    const draggedItem = updatedPosts[draggedItemIndex];

    updatedPosts.splice(draggedItemIndex, 1);
    updatedPosts.splice(index, 0, draggedItem);

    setPosts(updatedPosts);
    setDraggedItemIndex(index);
    setHoveredItemIndex(index);
  };

  const handleDrop = () => {
    if (!isLoggedIn()) return;
    if (draggedItemIndex === null || hoveredItemIndex === null) return;

    const reorderedPosts = posts.map((post, idx) => ({
      ...post,
      position: idx + 1,
    }));

    setPosts(reorderedPosts);
    setDraggedItemIndex(null);
    setHoveredItemIndex(null);
    setPageIndex(0);

    const positions = reorderedPosts.map((post) => post.id);
    dispatch(updatePostPosition(positions));
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
    setHoveredItemIndex(null);
  };

  const handleFavoriteTitleKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>,
    url: string,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    redirectToHTMLViewer(url);
  };

  const totalPages = Math.max(1, Math.ceil(posts.length / PAGE_SIZE));
  const actionButtonClassName =
    "cursor-pointer inline-flex size-8 items-center justify-center rounded-md text-muted-foreground outline-none hover:bg-accent hover:text-foreground dark:hover:bg-accent/50 dark:hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const paginatedData = useMemo(
    () => posts.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE),
    [posts, pageIndex],
  );

  const tableColumns = useMemo<ColumnDef<FavoriteRow, unknown>[]>(() => {
    const cols: ColumnDef<FavoriteRow, unknown>[] = [
      {
        id: "favoriteContent",
        header: "Atos em destaque",
        cell: ({ row }: CellContext<FavoriteRow, unknown>) => {
          const absoluteIndex = pageIndex * PAGE_SIZE + row.index;
          const item = row.original;

          return (
            <div
              draggable={isLoggedIn()}
              onDragStart={(event) => {
                if (!isLoggedIn()) return;
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData("text/plain", String(absoluteIndex));
                handleDragStart(absoluteIndex);
              }}
              onDragOver={(event) => handleDragOver(event, absoluteIndex)}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
              className={`${styles.favoriteRowContent} ${isLoggedIn() ? styles.favoriteRowContentDraggable : ""}`}
            >
              <span
                role="button"
                tabIndex={0}
                className={styles.favoriteTitleButton}
                onClick={() => redirectToHTMLViewer(item.url)}
                onKeyDown={(event) => handleFavoriteTitleKeyDown(event, item.url)}
              >
                {formatFileName(item.url)}
              </span>
              <p className={styles.favoriteDescription}>{item.description}</p>
              {isLoggedIn() && (
                <div className={styles.favoriteActions}>
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onDragStart={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        id="favoriteActions"
                        type="button"
                        draggable={false}
                        className={actionButtonClassName}
                        aria-label={`Abrir menu de ações para ${formatFileName(item.url)}`}
                      >
                        <MdMoreHoriz className="size-4" aria-hidden />
                      </DropdownMenuTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            id="editDescriptionButton"
                            onClick={() => handleEditButton(item.url, item.description)}
                          >
                            <MdCreate className="size-4" aria-hidden />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            id="removePinned"
                            variant="destructive"
                            onClick={() => handleFavoriteButton(item.url)}
                          >
                            <MdOutlinePushPin className="size-4" aria-hidden />
                            Remover
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenuPortal>
                    </DropdownMenu>
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
    ];

    return cols;
  }, [pageIndex, posts, draggedItemIndex, hoveredItemIndex]);

  return (
    <div className={`space-y-1 ${styles.homeFavoriteDataTableCardsSingleColumn}`}>
      {title && (
        <div className={styles.favoriteTitleContainer}>
          <h2 className="text-foreground">{title}</h2>
        </div>
      )}
      <DataTable<FavoriteRow>
        columns={tableColumns}
        data={error ? [] : paginatedData}
        emptyMessage={
          error ? "Não foi possível carregar os posts." : "Nenhum post fixado encontrado."
        }
        isLoading={Boolean(loading && posts.length > 0)}
        isLoadingInitial={Boolean(loading && posts.length === 0)}
        viewMode="cards"
        fixedViewMode="cards"
        paginationMode="server"
        serverPagination={{
          pageIndex,
          pageCount: totalPages,
          totalRowCount: posts.length,
          pageSize: PAGE_SIZE,
          onPageChange: setPageIndex,
        }}
      />

      <GetiDialog open={openDialog} onOpenChange={setOpenDialog}>
        <GetiDialogContent>
          <GetiDialogHeader className="space-y-1 px-6 pt-4 text-left">
            <GetiDialogTitle className="text-lg">Editar a descrição</GetiDialogTitle>
            <GetiDialogDescription>
              Escreva uma descrição para este ato fixado, com no máximo 200 caracteres.
            </GetiDialogDescription>
          </GetiDialogHeader>
          <div className="flex flex-col gap-2 px-6 pb-4 pt-4">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Escreva aqui"
              maxLength={200}
              autoFocus
            />

            <p className={`text-sm ${caracterLimit ? styles.errorText : "text-muted-foreground"}`}>{message}</p>
            <div className="flex justify-center">
              <GetiButton onClick={handleSubmitDescription}>Confirmar</GetiButton>
            </div>
          </div>
        </GetiDialogContent>
      </GetiDialog>
    </div>
  );
}
